import React, { useContext } from 'react'
import * as Icons from '../Icons'
import { TeamLogo } from '../Headers'
import { TeamContext } from '../../Context'
import {
  DesktopHeaderCon,
  DesktopItem,
  MobileHeaderCon,
  MobileItem,
  MobileTeamHeaderCon,
} from './styles'
import { InternalLink } from '../../Routing'
import { getTeamLocation, logEvent } from '../../Utils'
import { createUrl } from '../../Routing/urls'

type TeamLinkBarProps = {
  roster?: boolean
  stats?: boolean
  shop?: boolean
  hideOnMobile?: boolean
}

export const TeamTopLinkBar: React.FC<React.PropsWithChildren<TeamLinkBarProps>> = ({
  stats,
  roster,
  shop,
  hideOnMobile,
}) => {
  const { team, urlTickets, urlFullStats, urlShopGear  } = React.useContext(TeamContext)!
  if (!team) return null

  const log = (event: string, headerItem: string) => {
    logEvent(event, {
      page: team?.fullName,
      section: 'team_nav_header',
      headerItem,
    })
  }

  return (
    <>
      <MobileTeamHeaderCon>
        <InternalLink className="a" name="teams" params={{ id: team.fullName }}>
          <TeamLogo logo={team.logoSecondary || team.teamLogo} />
          <h1>
            {getTeamLocation(team.officialId)} {team.fullName}
          </h1>
        </InternalLink>
      </MobileTeamHeaderCon>
      <DesktopHeaderCon hideOnMobile={hideOnMobile}>
        <InternalLink className="a" name="teams" params={{ id: team.fullName }}>
          <TeamLogo logo={team.logoSecondary || team.teamLogo} />
          <h1>
            {getTeamLocation(team.officialId)} {team.fullName}
          </h1>
        </InternalLink>
        <div className="row">
          <DesktopItem
            href={urlTickets()}
            aria-label={`${team.fullName} schedule - ${team.fullName} tickets`}
            onClick={() => log('team_nav_click', 'tickets')}
          >
            <Icons.Tickets /> Schedule
          </DesktopItem>
          {roster && (
            <InternalLink
              className="a"
              name="roster"
              params={{ id: team.fullName }}
              aria-label={`${team.fullName} roster - ${team.fullName} roster`}
            >
              <Icons.Clipboard /> Roster
            </InternalLink>
          )}
          {stats && (
            <DesktopItem
              href={urlFullStats()}
              onClick={() => log('team_nav_click', 'stats')}
              aria-label={`${team.fullName} stats - ${team.fullName} stats`}
            >
              <Icons.BarChart /> Stats
            </DesktopItem>
          )}
          {shop && (
            <DesktopItem
              href={urlShopGear()}
              onClick={() => log('team_nav_click', 'shop')}
              aria-label={`${team.fullName} shop - ${team.fullName} shop`}
            >
              <Icons.ShoppingCart /> Shop
            </DesktopItem>
          )}
          <DesktopItem
            href={team.instagram}
            onClick={() => log('team_nav_click', 'instagram')}
            aria-label={`${team.fullName} instagram - ${team.fullName} instagram`}
          >
            <Icons.Instagram />
          </DesktopItem>

          <DesktopItem
            href={team.twitter}
            onClick={() => log('team_nav_click', 'twitter')}
            aria-label={`${team.fullName} twitter - ${team.fullName} twitter`}
          >
            <Icons.Twitter />
          </DesktopItem>
        </div>
      </DesktopHeaderCon>
    </>
  )
}

export const TeamMobileHeader: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { team } = React.useContext(TeamContext)!
  if (!team) return null

  return (
    <InternalLink className="a" name="teams" params={{ id: team.fullName }}>
      <TeamLogo logo={team.logoSecondary || team.teamLogo} />
      <h1>
        {getTeamLocation(team.officialId)} {team.fullName}
      </h1>
    </InternalLink>
  )
}

export const TeamMobileLinkBar: React.FC<
  React.PropsWithChildren<Omit<TeamLinkBarProps, 'hideOnMobile'>>
> = ({ stats, roster, shop }) => {
  const { team, urlTickets, urlFullStats, urlShopGear } = useContext(TeamContext)!
  if (!team) return null

  const log = (event: string, headerItem: string) => {
    logEvent(event, {
      page: team?.fullName,
      section: 'team_nav_header',
      headerItem,
    })
  }

  return (
    <MobileHeaderCon>
      <MobileItem
        href={urlTickets()}
        aria-label={`${team.fullName} schedule - ${team.fullName} tickets`}
        onClick={() => log('team_nav_click', 'tickets')}
      >
        <Icons.Tickets /> Schedule
      </MobileItem>
      {roster && team?.fullName && (
        <MobileItem
          href={createUrl('roster', { id: team.fullName.toLowerCase() })}
          aria-label={`${team.fullName} roster - ${team.fullName} roster`}
        >
          <Icons.Clipboard />
          <span>Roster</span>
        </MobileItem>
      )}
      {stats && (
        <MobileItem
          href={urlFullStats()}
          aria-label={`${team.fullName} stats - ${team.fullName} stats`}
          onClick={() => log('team_nav_click', 'stats')}
        >
          <Icons.BarChart />
          <span>Stats</span>
        </MobileItem>
      )}
      {shop && (
        <MobileItem
          href={urlShopGear()}
          aria-label={`${team.fullName} shop - ${team.fullName} shop`}
          onClick={() => log('team_nav_click', 'shop')}
        >
          <Icons.ShoppingCart />
          <span>Shop</span>
        </MobileItem>
      )}
      <MobileItem
        href={team.instagram}
        onClick={() => log('team_nav_click', 'instagram')}
        aria-label={`${team.fullName} instagram - ${team.fullName} instagram`}
      >
        <Icons.Instagram />
        <span>Instagram</span>
      </MobileItem>

      <MobileItem
        href={team.twitter}
        onClick={() => log('team_nav_click', 'twitter')}
        aria-label={`${team.fullName} twitter - ${team.fullName} twitter`}
      >
        <Icons.Twitter />
        <span>Twitter</span>
      </MobileItem>
    </MobileHeaderCon>
  )
}
